import { DataGridInstanceName } from '../keys';
import { VersionedGridState } from 'components/common/dataGrid/columnsFactory/types';
import { addInspectionNumberColumn } from './addInspectionNumberColumn';
import { enforceIssueColumnOrder } from './enforceIssueColumnOrder';
import { addWorkflowColumn } from './addWorkflowColumn';

type MigrationFn = (state: VersionedGridState) => VersionedGridState;

interface DataGridMigrationConfig {
  targetVersion: number;
  migrations: MigrationFn[];
}

const MIGRATIONS: Partial<Record<DataGridInstanceName, DataGridMigrationConfig>> = {
  [DataGridInstanceName.issue]: {
    targetVersion: 4,
    migrations: [
      (state) => enforceIssueColumnOrder(state).next,
      (state) => addWorkflowColumn(state).next,
      // v3 -> v4: re-run on users whose v2 state was restored to the grid
      // before the migration could place `workflow` before `stage`
      // (MUI appends unknown fields to the end of orderedFields).
      (state) => addWorkflowColumn(state).next,
    ],
  },
};

export function getTargetVersion(instanceName: DataGridInstanceName): number {
  return MIGRATIONS[instanceName]?.targetVersion ?? 0;
}

export function applyMigrations(
  instanceName: DataGridInstanceName,
  state: VersionedGridState
): VersionedGridState {
  const config = MIGRATIONS[instanceName];
  if (!config) return state;

  const currentVersion = state._version ?? 0;
  if (currentVersion >= config.targetVersion) return state;

  let migrated = { ...state };

  for (let v = currentVersion; v < config.targetVersion; v++) {
    const migrationFn = config.migrations[v];
    if (migrationFn) {
      migrated = migrationFn(migrated);
    }
  }

  return migrated;
}

export function hasMigrations(instanceName: DataGridInstanceName): boolean {
  return instanceName in MIGRATIONS;
}
